/**
 * NavVisibilityContext — lets a full-screen child (e.g. the Parrot Method
 * carousel reached from Settings) hide HomeLayout's bottom nav bar while it is
 * mounted, matching iOS where that screen is a pushed full-screen route with no
 * tab bar.
 *
 * HomeLayout provides it and reads `hidden` to decide whether to render NavBar.
 * Children call `useHideNavBar()` in an effect to hide while mounted and restore
 * on unmount. The hook is a no-op when no provider is present (e.g. the
 * standalone /method-explanation route that lives outside HomeLayout), so it is
 * always safe to call.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  FC,
  ReactNode,
} from "react";

interface NavVisibility {
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
}

const NavVisibilityContext = createContext<NavVisibility | null>(null);

export const NavVisibilityProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [hidden, setHidden] = useState(false);
  const value = useMemo(() => ({ hidden, setHidden }), [hidden]);
  return (
    <NavVisibilityContext.Provider value={value}>
      {children}
    </NavVisibilityContext.Provider>
  );
};

/** Read whether the nav bar is currently hidden (used by HomeLayout). */
// eslint-disable-next-line react-refresh/only-export-components
export const useNavHidden = (): boolean =>
  useContext(NavVisibilityContext)?.hidden ?? false;

/**
 * Hide the bottom nav bar for as long as the calling component is mounted.
 * Safe to call outside a provider (no-op).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useHideNavBar = (): void => {
  // Depend on the stable setter only — NOT the whole context value. The provider
  // re-memoizes its value when `hidden` changes, so depending on the object
  // would tear down + re-run this effect on the very state change it triggers
  // (flashing the nav bar). The useState setter identity is stable, so this
  // effect runs once on mount and cleans up only on unmount.
  const setHidden = useContext(NavVisibilityContext)?.setHidden;
  useEffect(() => {
    if (!setHidden) return;
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);
};
